import Banner from "@/components/Banner";
import SearchBar from "@/components/SearchBar";
import FilterBar from "@/components/FilterBar";
import ProductSection from "@/components/ProductSection";
import { useTranslation } from "react-i18next";
import { Product } from "@/types/product";
import { ProductFilters } from "@/types/filters";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";

const Index = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("q");
  
  const [filters, setFilters] = useState<ProductFilters>({
    hairType: [],
    features: [],
    category: undefined,
    searchTerm: searchQuery || undefined,
  });

  useEffect(() => {
    setFilters(prev => ({ ...prev, searchTerm: searchQuery || undefined }));
  }, [searchQuery]);
  
  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products', searchQuery, filters],
    queryFn: async () => {
      console.log('Fetching products with filters:', filters);
      let query = supabase
        .from('products')
        .select('*, reviews(rating, comment, purchase_location, user_id, profiles(username))');
      
      if (filters.searchTerm) {
        query = query.or(`name.ilike.%${filters.searchTerm}%,brand.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
      }

      if (filters.hairType && filters.hairType.length > 0) {
        query = query.overlaps('hair_type', filters.hairType);
      }

      if (filters.features && filters.features.length > 0) {
        query = query.overlaps('product_features', filters.features);
      }

      if (filters.category) {
        query = query.eq('product_category', filters.category);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching products:', error);
        throw error;
      }
      
      console.log('Fetched products:', data);
      return data as Product[];
    }
  });

  const handleFilterChange = (newFilters: ProductFilters) => {
    console.log('Updating filters:', newFilters);
    setFilters(newFilters);
  };

  if (error) {
    console.error('Error loading products:', error);
    return <div className="container mx-auto px-4 py-8">Error loading products</div>;
  }

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  const noResults = products && products.length === 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col gap-6">
        <SearchBar />
        <FilterBar filters={filters} onFilterChange={handleFilterChange} />
        {noResults ? (
          <div className="text-center py-8">
            <p className="text-lg text-muted-foreground">
              {t('products.noResults', { query: searchQuery })}
            </p>
          </div>
        ) : (
          <ProductSection 
            title={searchQuery ? t('products.searchResults') : t('products.allProducts')} 
            products={products || []}
          />
        )}
      </div>
    </div>
  );
};

export default Index;